// src/components/ErrorBoundary.jsx
import React from "react";
import { FiAlertCircle, FiRefreshCw, FiHome } from "react-icons/fi";
import { logError } from "../utils/logger";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logError("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px",
            background: "#F8FAFC",
            fontFamily:
              "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
          }}
        >
          <div
            style={{
              maxWidth: 600,
              width: "100%",
              background: "#fff",
              borderRadius: 12,
              padding: "40px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <FiAlertCircle size={64} color="#EF4444" style={{ marginBottom: 16 }} />
              <h1
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#1F2937",
                  margin: "0 0 8px 0",
                }}
              >
                Something went wrong
              </h1>
              <p style={{ fontSize: 16, color: "#6B7280", margin: 0 }}>
                An unexpected error occurred. Please try again or contact support if the problem persists.
              </p>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: "#F9FAFB",
                  borderRadius: 8,
                  border: "1px solid #E5E7EB",
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 8,
                  }}
                >
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    fontSize: 12,
                    color: "#DC2626",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 24,
                justifyContent: "center",
              }}
            >
              <button
                onClick={this.handleReset}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 24px",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#fff",
                  background: "#3366CC",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#2555B8")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#3366CC")}
              >
                <FiRefreshCw size={18} />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 24px",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#334155",
                  background: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#E2E8F0")}
                onMouseOut={(e) => (e.currentTarget.style.background = "#F1F5F9")}
              >
                <FiHome size={18} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
